/**
 * Paginación por cursor del hilo de mensajes del inbox.
 *
 * Contrato (backend 2026-08-28, `CONTRACT-inbox-messages.md` § 1):
 *
 *   GET /inbox/conversations/:id/messages?limit=50[&before=<message_id>]
 *   → { success,
 *       data: MessageResponse[],   // ASC: viejos arriba, nuevos abajo
 *       meta: { total, limit, offset, has_more, next_cursor? } }
 *
 * Por qué existe este módulo: antes la respuesta traía SÓLO `data`, así que un
 * hilo cortado se veía IDÉNTICO a uno completo. Una conversación de producción
 * con 101 mensajes vivos mostraba los últimos 50 y no había ninguna forma —ni
 * para el usuario ni para el código— de saber que faltaban cuatro meses de
 * historia. `meta` es lo que hace observable esa diferencia.
 *
 * Las tres reglas que este módulo hace cumplir:
 *
 * 1. `has_more` es la ÚNICA fuente de verdad sobre si queda historia atrás.
 *    `data.length` no lo es: una página completa no implica que haya más, y
 *    una corta no implica que no.
 * 2. `next_cursor` es el mensaje MÁS VIEJO de la página, y sólo viene cuando
 *    `has_more` es true. Mandar el más nuevo devuelve la misma página para
 *    siempre.
 * 3. `meta` puede NO VENIR: el backend está en `main` pero todavía sin deploy.
 *    Eso NO es lo mismo que "no hay más" y por eso se distingue con `present`.
 */

/** Lectura normalizada del `meta` del listado de mensajes. */
export type MessagesMeta = {
  /**
   * Mensajes VIVOS de la conversación (mismo filtro `is_deleted=false` que la
   * página). `null` cuando el servidor no lo dijo — el llamador cae a
   * `items.length`, pero sabiendo que es una estimación y no un total.
   */
  total: number | null;
  /** ¿Quedan mensajes MÁS VIEJOS que los de esta página? */
  hasMore: boolean;
  /**
   * ID del mensaje más viejo de ESTA página: el ancla para pedir la anterior
   * con `?before=`. `null` cuando el servidor no lo mandó (que es lo esperable
   * cuando `hasMore` es false).
   */
  nextCursor: string | null;
  /**
   * `false` cuando la respuesta no trajo `meta` en absoluto — el backend
   * todavía no está deployado. Se distingue de `hasMore:false` a propósito:
   * "no sé" y "no hay" llevan a UIs distintas, y confundirlos es exactamente
   * el bug que este contrato viene a matar.
   */
  present: boolean;
};

const AUSENTE: MessagesMeta = {
  total: null,
  hasMore: false,
  nextCursor: null,
  present: false,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * `meta` puede llegar arriba de todo (`{ data, meta }`) o anidado dentro del
 * envelope (`{ data: { data, meta } }`) según el endpoint — el backend es
 * inconsistente con los envelopes y el SDK miente sobre la forma, así que se
 * prueban las dos posiciones antes de darlo por ausente.
 */
function findMeta(payload: unknown): Record<string, unknown> | null {
  const top = asRecord(payload);
  if (!top) return null;
  const direct = asRecord(top.meta);
  if (direct) return direct;
  const inner = asRecord(top.data);
  return inner ? asRecord(inner.meta) : null;
}

function readCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // Un backend Go serializando int64 grande, o un proxy que lo pasa a string.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return false;
}

function readCursor(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Lee el `meta` del listado de mensajes tolerando que no venga.
 *
 * No infiere nada de `data.length`: si el servidor no habló, `present` es
 * `false` y el llamador decide. Inferir "hay más porque la página vino llena"
 * es justamente el heurístico roto que causó el incidente (una ráfaga de 50
 * mensajes llenó la primera página, y la página llena era indistinguible de
 * un hilo de exactamente 50).
 */
export function readMessagesMeta(payload: unknown): MessagesMeta {
  const meta = findMeta(payload);
  if (!meta) return AUSENTE;

  const hasMore = readBool(meta.has_more);
  return {
    total: readCount(meta.total),
    hasMore,
    // El cursor sólo es utilizable si además hay más atrás. Si el servidor
    // mandara `next_cursor` con `has_more:false` (no debería), ignorarlo evita
    // pedir una página que ya sabemos vacía.
    nextCursor: hasMore ? readCursor(meta.next_cursor) : null,
    present: true,
  };
}

/**
 * ¿Se puede pedir otra página hacia atrás?
 *
 * Exige el cursor además del flag: sin ancla no hay pedido posible, y ofrecer
 * "cargar anteriores" sin poder cumplirlo es peor que no ofrecerlo. El caso
 * `hasMore && !nextCursor` no debería ocurrir contra este backend, pero si
 * ocurre tiene que frenar el paginado en vez de reintentar en loop.
 */
export function canLoadOlder(meta: MessagesMeta): boolean {
  return meta.hasMore && meta.nextCursor !== null;
}

/**
 * Cuántos mensajes quedan atrás de los ya cargados, o `null` si no se sabe.
 *
 * Se usa para el aviso "hay N mensajes anteriores". Devuelve `null` —y no 0—
 * cuando falta el total: la UI tiene que poder decir "hay más" sin inventar un
 * número.
 */
export function remainingOlderCount(
  meta: MessagesMeta,
  loadedCount: number,
): number | null {
  if (meta.total === null) return null;
  return Math.max(0, meta.total - loadedCount);
}

/** Un mensaje, para los helpers de merge: sólo hace falta la identidad. */
type Identifiable = { id?: string | null };

/**
 * Une un bloque de mensajes MÁS VIEJOS con el hilo que ya se tenía.
 *
 * El bloque nuevo va ADELANTE porque el backend entrega cada página en ASC
 * (viejos arriba). Se deduplica por `id` dando prioridad a la copia que ya
 * estaba en el hilo: esa es la que puede tener parches locales de realtime
 * (ticks de estado, transcripción, media resuelta) que la respuesta paginada
 * no necesariamente trae.
 *
 * Con `before` no debería haber solapamiento —el keyset ancla en una fila—
 * pero deduplicar igual es barato y convierte un bug de cursor en una
 * degradación invisible en vez de mensajes repetidos en pantalla.
 */
export function mergeOlderMessages<T extends Identifiable>(
  older: T[],
  existing: T[],
): T[] {
  const seen = new Set<string>();
  for (const message of existing) {
    if (typeof message.id === 'string') seen.add(message.id);
  }

  const prefix: T[] = [];
  for (const message of older) {
    if (typeof message.id === 'string') {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
    }
    prefix.push(message);
  }

  return prefix.length === 0 ? existing : [...prefix, ...existing];
}

/** Foto del scroll tomada JUSTO ANTES de pedir una página vieja. */
export interface PrependAnchor {
  /** Alto acumulado del scroller en el momento del pedido. */
  scrollHeight: number;
  /** Posición del scroll en el momento del pedido. */
  scrollTop: number;
  /** `offsetTop` del nodo ancla en el momento del pedido. */
  offsetTop: number;
}

/**
 * Dónde tiene que quedar el scroll después de prependear historia, o `null`
 * si no hay nada que corregir.
 *
 * El objetivo es que el renglón que el agente estaba mirando NO se mueva. Su
 * posición en pantalla es `offsetTop - scrollTop`, así que conservarla es
 * `scrollTop' = scrollTop + (offsetTop' - offsetTop)`.
 *
 * Dos decisiones que parecen detalles y no lo son:
 *
 * - **Se mide por el nodo ancla, no por `scrollHeight`.** El alto total del
 *   scroller también crece cuando entra un mensaje por la COLA mientras se
 *   trae la página vieja, y ahí el delta mentiría y patearía la vista. El
 *   `offsetTop` del ancla sólo se mueve por lo que se insertó ARRIBA — que es
 *   exactamente lo que hay que compensar, incluidos los separadores de día que
 *   se corren cuando la página vieja cae en la misma fecha. `scrollHeight`
 *   queda de red para cuando el nodo desapareció del DOM.
 *
 * - **La fórmula es ABSOLUTA, no `scrollTop += delta`.** Los navegadores hacen
 *   su propio *scroll anchoring* al insertar contenido arriba: con la forma
 *   relativa el ajuste se sumaría dos veces y la vista pegaría un salto. Ésta
 *   no lee el `scrollTop` actual, así que aplicarla sobre un scroll ya
 *   corregido da el mismo resultado.
 */
export function anchoredScrollTop(
  anchor: PrependAnchor,
  actual: { nodeOffsetTop: number | null; scrollHeight: number },
): number | null {
  const delta =
    actual.nodeOffsetTop !== null
      ? actual.nodeOffsetTop - anchor.offsetTop
      : actual.scrollHeight - anchor.scrollHeight;

  // delta <= 0 significa que no se insertó nada arriba: tocar el scroll ahí
  // sería moverle la vista al agente sin motivo.
  if (!Number.isFinite(delta) || delta <= 0) return null;
  return anchor.scrollTop + delta;
}

/**
 * ¿Este error es el `400 invalid_cursor` del contrato?
 *
 * El backend responde 400 —y NO la primera página— ante un `before` corrupto,
 * inexistente o de otra conversación. Es deliberado: degradar en silencio
 * devolvería lo que el cliente ya tenía, en un loop que desde afuera se ve
 * como "no carga más". Detectarlo importa porque la reacción correcta es
 * FRENAR el paginado y avisar, nunca reintentar sin cursor.
 */
export function isInvalidCursorError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === 'string') return error.includes('invalid_cursor');
  if (typeof error !== 'object') return false;
  const value = error as { code?: unknown; error?: unknown; message?: unknown };
  if (value.code === 'invalid_cursor' || value.error === 'invalid_cursor') return true;
  return typeof value.message === 'string' && value.message.includes('invalid_cursor');
}
